import { Select, SelectOption } from '@leasequery/lq.react-component-library';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReportType } from '../../../../models';
import {
  ReportSettingField,
  ReportTypeSetting,
} from '../../../../models/reportTypeSetting';
import {
  buildDropdownListFromReportSettingField,
  getReportSettingField,
  getReportypeSetting,
} from '../helpers/buildReportSettingData';
import { ReportSettingPAndLState } from '../PAndL';
import * as Styled from './styled';

interface AllocationSelectorProps {
  onChange: (updatedValue: ReportSettingPAndLState) => void;
  reportSettingState: ReportSettingPAndLState;
  reportTypeSetting: ReportTypeSetting;
}

const getSubAllocations = (
  selectedAllocationOption: SelectOption<string>,
  subViewBySetting: ReportSettingField,
): SelectOption<string>[] => {
  return subViewBySetting.reportSettingFieldValues.reduce((acc, val) => {
    if (`${val.parrentId}` === selectedAllocationOption?.value) {
      acc.push({
        key: val.id,
        value: val.value,
        label: val.name,
      });
    }
    return acc;
  }, [] as SelectOption<string>[]);
};

function AllocationSelector({
  onChange,
  reportSettingState: { selectedViewBy, selectedSubViewBy },
  reportTypeSetting,
}: AllocationSelectorProps): JSX.Element {
  const reportSetting = useMemo(
    () =>
      getReportypeSetting(
        reportTypeSetting,
        ReportType.effectOfStandardAdoptionPAndL,
      ),
    [reportTypeSetting],
  );
  const subViewBySetting = useMemo(
    () => getReportSettingField(reportSetting, 'ViewByItemOptions'),
    [reportSetting],
  );

  const [viewByOptions, setViewByOptions] = useState<SelectOption<string>[]>(
    [],
  );
  const [subViewByOptions, setSubViewByOptions] = useState(
    getSubAllocations(selectedViewBy, subViewBySetting),
  );

  const viewByChangeHandler = useCallback(
    (option: SelectOption<string>): void => {
      if (option.value === '') {
        onChange({
          selectedViewBy: option,
          selectedSubViewBy: null,
          isValid: true,
        });
        setSubViewByOptions(null);
        return;
      }

      // There is an alocation is selected
      // => re-build the sub-allocation dropdown
      const subAllocations = getSubAllocations(option, subViewBySetting);
      setSubViewByOptions(subAllocations);
      const calculatedSubViewBy =
        selectedViewBy?.value === option.value ? selectedSubViewBy : null;

      onChange({
        selectedViewBy: option,
        selectedSubViewBy: calculatedSubViewBy,
        isValid: !!calculatedSubViewBy,
      });
    },
    [onChange, selectedSubViewBy, selectedViewBy?.value, subViewBySetting],
  );
  const subViewByChangeHandler = useCallback(
    (option: SelectOption<string>): void => {
      onChange({ selectedViewBy, selectedSubViewBy: option, isValid: true });
    },
    [onChange, selectedViewBy],
  );

  useEffect(() => {
    const viewBySetting = getReportSettingField(reportSetting, 'ViewByOptions');
    const viewByFieldValues =
      buildDropdownListFromReportSettingField(viewBySetting);
    setViewByOptions(viewByFieldValues);
    viewByChangeHandler(selectedViewBy || viewByFieldValues[0]);
  }, [reportSetting, selectedSubViewBy, selectedViewBy, viewByChangeHandler]);

  return (
    <Styled.AllocationSelector>
      <Select
        label="View By"
        name="viewby"
        options={viewByOptions}
        onChange={viewByChangeHandler}
        value={selectedViewBy}
      />
      {subViewByOptions && !!subViewByOptions.length && (
        <Select
          placeholder="--Select--"
          className="subViewBy"
          label="Allocation"
          name="subviewby"
          options={subViewByOptions}
          onChange={subViewByChangeHandler}
          value={selectedSubViewBy}
        />
      )}
    </Styled.AllocationSelector>
  );
}

export default AllocationSelector;
